import { useId } from "react";
import { Avatar } from "@/components/ui/misc";
import type { Channel, ChannelAccount } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Geometry of the Malfak mark (64×64), shared with public/favicon.svg and the PWA icons. */
export const MARK = {
  /** "ملف" (file) + a conversation: a document with a speech tail, its corner folded in saffron. */
  file: "M22.5 13.5H37L49 25.5V40.5Q49 47 42.5 47H29.5L21.6 53.2Q19.6 54.6 19.9 52.2L20.5 46.6Q15.5 45.4 15.5 40V20.5Q15.5 13.5 22.5 13.5Z",
  fold: "M37 13.5V21.5Q37 25.5 41 25.5H49Z",
  /** The "M" of Malfak, written as a little wave inside the file. */
  m: "M23 37.5L27.6 28.5L32.2 34.5L36.8 28.5L41.4 37.5",
} as const;

/**
 * The Malfak mark: an indigo tile holding a white file-shaped speech bubble with an "M" inside and
 * a saffron folded corner. `tone="light"` draws the tile as glass for use on the brand gradient.
 */
export function LogoMark({ size = 32, className, tone = "brand", animated }: { size?: number; className?: string; tone?: "brand" | "light"; animated?: boolean }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={cn("shrink-0", animated && "logo-animated", className)} aria-hidden>
      <defs>
        <linearGradient id={`${id}g`} x1="8" y1="4" x2="58" y2="62" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#8A7BFF" />
          <stop offset=".5" stopColor="#5A46EF" />
          <stop offset="1" stopColor="#3B24B9" />
        </linearGradient>
        <radialGradient id={`${id}h`} cx="18" cy="10" r="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff" stopOpacity=".32" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      {tone === "brand" ? (
        <>
          <rect width="64" height="64" rx="18" fill={`url(#${id}g)`} />
          <rect width="64" height="64" rx="18" fill={`url(#${id}h)`} />
        </>
      ) : (
        <rect x=".5" y=".5" width="63" height="63" rx="17.5" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.28)" />
      )}
      <path className="logo-file" d={MARK.file} fill="#fff" />
      <path className="logo-fold" d={MARK.fold} fill="#F2A93B" />
      <path className="logo-m" d={MARK.m} fill="none" stroke="#4F3FE0" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" pathLength={1} />
    </svg>
  );
}

/** "Malfak ملفك": Latin and Arabic set in the same display family, the Arabic a step quieter. */
export function Wordmark({ className, size = 16, tone = "ink" }: { className?: string; size?: number; tone?: "ink" | "white" }) {
  return (
    <span className={cn("font-display inline-flex items-baseline gap-1.5 whitespace-nowrap leading-none", className)} style={{ fontSize: size }}>
      <span lang="en" dir="ltr" className={cn("font-semibold tracking-[-0.02em]", tone === "white" ? "text-white" : "text-ink")}>
        Malfak
      </span>
      <span lang="ar" dir="rtl" className={cn("font-medium", tone === "white" ? "text-white/75" : "text-ink-3")} style={{ fontSize: size * 0.94 }}>
        ملفك
      </span>
    </span>
  );
}

export function Logo({ size = 32, withText = true, className, sub, tone = "ink" }: { size?: number; withText?: boolean; className?: string; sub?: string; tone?: "ink" | "white" }) {
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <LogoMark size={size} tone={tone === "white" ? "light" : "brand"} />
      {withText && (
        <span className="leading-tight">
          <Wordmark size={Math.max(15, Math.round(size * 0.5))} tone={tone} />
          {sub && <span className={cn("mt-1 block text-[11px]", tone === "white" ? "text-white/70" : "text-ink-3")}>{sub}</span>}
        </span>
      )}
    </span>
  );
}

/** Small sign-off used in the sidebar, auth and onboarding footers. */
export function BrandFooter({ className, tagline }: { className?: string; tagline: string }) {
  return (
    <div className={cn("flex items-center gap-1.5 text-[11px] text-ink-3", className)}>
      <LogoMark size={14} />
      <Wordmark size={11.5} />
      <span aria-hidden>·</span>
      <span className="truncate">{tagline}</span>
    </div>
  );
}

const CHANNEL_COLORS: Record<Channel, string> = {
  instagram: "linear-gradient(45deg,#f9b233 0%,#ee5a6f 45%,#c13584 70%,#6a4bd8 100%)",
  facebook_page: "#1877f2",
  messenger: "linear-gradient(45deg,#0a7cff,#a334fa,#ff5e6c)",
  whatsapp: "#25d366",
  threads: "#111111",
  tiktok: "#111111",
};

/** Generic channel glyphs (simple geometric marks, not the platforms' logos). */
export function ChannelIcon({ channel, size = 16, className }: { channel: Channel; size?: number; className?: string }) {
  const s = { width: size, height: size };
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <svg viewBox="0 0 24 24" style={s} className={className} aria-hidden>
      {channel === "instagram" && (
        <g {...common}>
          <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.2" cy="6.8" r=".6" fill="currentColor" />
        </g>
      )}
      {channel === "facebook_page" && <path {...common} d="M14.5 8H16V4.5h-2.2A3.8 3.8 0 0 0 10 8.3V11H8v3.5h2V20h3.5v-5.5h2.2L16 11h-2.5V8.9c0-.5.4-.9 1-.9Z" />}
      {channel === "messenger" && (
        <g {...common}>
          <path d="M12 3.5c-4.8 0-8.5 3.5-8.5 8 0 2.5 1.2 4.7 3 6.1V20.5l2.8-1.5c.8.2 1.7.3 2.7.3 4.8 0 8.5-3.5 8.5-8s-3.7-7.8-8.5-7.8Z" />
          <path d="m7.5 13.5 3-3.2 2.3 2.2 3.7-2.2-3 3.2-2.3-2.2Z" />
        </g>
      )}
      {channel === "whatsapp" && (
        <g {...common}>
          <path d="M4 20l1.2-3.9A8.5 8.5 0 1 1 8 19Z" />
          <path d="M9.3 8.6c.3 2.8 2.6 5.3 5.6 5.9l.9-1.2-1.6-.9-.8.8a4 4 0 0 1-2.2-2.2l.8-.8-.9-1.6Z" />
        </g>
      )}
      {channel === "threads" && <path {...common} d="M16.5 11.2c-.4-2.6-2.1-3.7-4.3-3.7-2.6 0-4.2 1.9-4.2 4.6s1.6 4.9 4.6 4.9c2.6 0 4.4-1.4 4.4-3.5 0-2.4-2.5-3-4.4-2.9-1.7.1-2.6.9-2.6 1.9 0 1 .9 1.6 2 1.6 1.8 0 2.8-1.4 2.8-4" />}
      {channel === "tiktok" && <path {...common} d="M14 4v10.5a3.5 3.5 0 1 1-3-3.4M14 4c.4 2.3 2 3.9 4.5 4.1" />}
    </svg>
  );
}

/** A small coloured disc with the channel glyph: used as an avatar badge. */
export function ChannelBadge({ channel, size = 16, className }: { channel: Channel; size?: number; className?: string }) {
  return (
    <span className={cn("inline-grid shrink-0 place-items-center rounded-full text-white ring-2 ring-surface", className)} style={{ width: size, height: size, background: CHANNEL_COLORS[channel] }} title={channel}>
      <ChannelIcon channel={channel} size={size * 0.68} />
    </span>
  );
}

/** Per-account colour (calendar chips, chart series): stable per account id. */
const ACCOUNT_COLORS = ["#4f3fe0", "#d6456f", "#3c9a4e", "#c97520", "#2f86d6", "#0e9aa7"];
export function accountColor(accounts: { id: string }[] | undefined, id: string): string {
  const i = accounts?.findIndex((a) => a.id === id) ?? -1;
  if (i >= 0) return ACCOUNT_COLORS[i % ACCOUNT_COLORS.length]!;
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return ACCOUNT_COLORS[h % ACCOUNT_COLORS.length]!;
}

export function AccountAvatar({ account, size = 32, className }: { account: Pick<ChannelAccount, "username" | "avatarUrl" | "channel" | "displayName">; size?: number; className?: string }) {
  return <Avatar src={account.avatarUrl} name={account.displayName ?? account.username} size={size} className={className} badge={<ChannelBadge channel={account.channel} size={Math.max(12, Math.round(size * 0.4))} />} />;
}
